import { DesktopDatePicker as MuiDatePicker, DatePickerProps as MUIDatePickerProps } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { CalendarAlt } from '@assets/svgs';
import { TextField, TextFieldExtendedProps } from '@components/textField';
import { Box, Theme } from '@mui/material';
import { adjustFontSize } from '@theme/styleOverrides/typography';
import { useEffect, useState } from 'react';

// Wrapper component to filter out MUI internal props
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CalendarIcon = ({ ownerState, ...props }: any) => <CalendarAlt {...props} />;

export const datePickerStyles = {
  '& .MuiOutlinedInput-root': {
    padding: '2px 12px 2px 4px',
  },
  '& .MuiIconButton-root': {
    padding: '0px',
    color: 'text.subdued',
  },
  '& .MuiOutlinedInput-root input': {
    padding: '0',
    fontSize: ({ palette }: Theme) => `${adjustFontSize(14, palette.fontSize)}px`,
  },
};

export interface DatePickerProps extends MUIDatePickerProps<Date> {
  disabled?: boolean;
  textFieldProps?: TextFieldExtendedProps;
  fullWidth?: boolean;
  inputLabel?: string;
  clearable?: boolean;
}

function DateTextField(props: TextFieldExtendedProps) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'top',
        height: '100%',
        width: '100%',
        justifyContent: 'flex-end',
        verticalAlign: 'top',
      }}
    >
      <TextField {...props} />
    </Box>
  );
}

export const DatePicker = ({
  disabled,
  fullWidth,
  openTo,
  textFieldProps = { placeholder: 'MM/DD/YYYY', autoComplete: 'off' },
  clearable,
  defaultValue,
  value,
  ...other
}: DatePickerProps) => {
  const [muiValue, setMuiValue] = useState<Date | null>(defaultValue ?? value ?? null);

  useEffect(() => {
    setMuiValue(value ?? null);
  }, [value]);

  const {
    open,
    onClose,
    onOpen,
    onChange,
    onAccept,
    onError,
    minDate,
    maxDate,
    shouldDisableDate,
    disableFuture,
    disablePast,
    loading,
    readOnly,
    inputRef,
    name,
    label,
    reduceAnimations,
    // capture any other props to pass along to MUI DatePicker
    ...unknownOther
  } = other;

  const textFieldCombinedProps = {
    ...textFieldProps,
    ...other.slotProps?.textField,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <MuiDatePicker
        sx={datePickerStyles}
        openTo={openTo}
        value={muiValue}
        format='MM/dd/yyyy'
        slots={{
          openPickerIcon: CalendarIcon,
          textField: DateTextField,
        }}
        slotProps={{
          layout: {
            sx: {
              color: ({ palette }) => (palette.theme == 'lio' ? 'black' : `${palette.text.main}`),

              // TODO move into a theme style override

              '& .MuiPickersDay-root': {
                borderColor: ({ palette }) => `${palette.primary.main} !important`,
                color: ({ palette }) => (palette.theme == 'lio' ? 'black' : `${palette.text.main}`),
              },

              '.MuiTypography-root': {
                color: ({ palette }) => (palette.theme == 'lio' ? 'black' : `${palette.text.main}`),
              },

              '& .MuiSvgIcon-root': {
                color: ({ palette }) => (palette.theme == 'lio' ? 'black' : `${palette.text.main}`),
              },
              '& .MuiPickersDay-root.Mui-selected ': {
                color: 'white',
              },
              '& .MuiPickersDay-root.Mui-disabled ': {
                color: 'gray !important',
              },
            },
          },
          field: { clearable: clearable ?? false },

          inputAdornment: { position: 'start' },
          textField: {
            fullWidth,

            name: textFieldCombinedProps?.name,
            label: textFieldCombinedProps?.label,
            error: textFieldCombinedProps?.error,
            disabled: textFieldCombinedProps?.disabled || disabled,
            required: textFieldCombinedProps?.required,
            margin: textFieldCombinedProps?.margin,
            size: textFieldCombinedProps?.size,
            type: textFieldCombinedProps?.type,
            multiline: textFieldCombinedProps?.multiline,
            rows: textFieldCombinedProps?.rows,
            maxRows: textFieldCombinedProps?.maxRows,
            minRows: textFieldCombinedProps?.minRows,
            placeholder: textFieldCombinedProps?.placeholder || 'MM/DD/YYYY',
            inputRef: textFieldCombinedProps?.inputRef,
            helperText: textFieldCombinedProps?.helperText,
            externalIcon: textFieldCombinedProps?.externalIcon,

            // // EC: i don't think we need this (yet) sx: textFieldProps?.sx,
            slotProps: {
              inputProps: {
                autoComplete: textFieldProps?.autoComplete,
                onBlur: textFieldProps?.onBlur,
                onFocus: textFieldProps?.onFocus,
                placeholder: textFieldProps?.placeholder || 'MM/DD/YYYY',
                disabled: textFieldProps?.disabled,
              },
            },
            inputProps: textFieldProps?.slotProps?.htmlInput,
            FormHelperTextProps: textFieldProps?.slotProps?.formHelperText,

            // The following are custom LIO attributes outside of MUI (included in TextFieldExtendedProps)
            inputLabel: textFieldProps?.inputLabel,
            inputLabelIcon: textFieldProps?.inputLabelIcon,
            tooltipTitle: textFieldProps?.tooltipTitle,
            helperLabel: textFieldProps?.helperLabel,
            deprecated: textFieldProps.deprecated,
            requiredBeforeBind: textFieldProps?.requiredBeforeBind,
            config: textFieldProps?.config,
          } as TextFieldExtendedProps,
        }}
        disabled={disabled}
        open={open}
        onClose={onClose}
        onOpen={onOpen}
        onChange={onChange}
        onAccept={onAccept}
        onError={onError}
        minDate={minDate}
        maxDate={maxDate}
        shouldDisableDate={shouldDisableDate}
        disableFuture={disableFuture}
        disablePast={disablePast}
        loading={loading}
        readOnly={readOnly}
        inputRef={inputRef}
        name={name}
        label={label}
        reduceAnimations={reduceAnimations}
        {...unknownOther}
      />
    </LocalizationProvider>
  );
};
